import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SkipSelf,
  SimpleChanges,
} from "@angular/core";
import {
  ControlContainer,
  FormArray,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  EmployeeVcard,
  EmployeeVcardRowValue,
  EmployeeVcardValidationError,
  NoteInterval,
} from "../../employees/employees.interface";
import { CalendarRangeHeaderComponent } from "../range-calendar/calendar-range-header/calendar-range-header.component";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../error-state-matcher";

@Component({
  selector: "app-employee-vcard",
  templateUrl: "./employee-vcard.component.html",
  styleUrls: ["./employee-vcard.component.scss"],
  standalone: false,
})
export class EmployeeVcardComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) parentForm!: UntypedFormGroup;
  @Input() entries: EmployeeVcard[] = [];
  @Input() loadVersion = 0;
  @Input() errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  @Output() changed = new EventEmitter<void>();

  readonly calendarRangeHeader = CalendarRangeHeaderComponent;

  private lastLoadVersion = 0;
  private valueChangesSubscribed = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() @SkipSelf() private readonly controlContainer: ControlContainer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["loadVersion"] &&
      this.loadVersion > 0 &&
      this.loadVersion !== this.lastLoadVersion
    ) {
      this.lastLoadVersion = this.loadVersion;
      this.syncEntries(this.entries ?? []);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get notes(): UntypedFormArray {
    return this.vcardArray;
  }

  trackByIndex(index: number): number {
    return index;
  }

  syncEntries(entries: EmployeeVcard[]): void {
    const array = this.vcardArray;
    array.clear({ emitEvent: false });
    const list = entries ?? [];
    if (list.length === 0) {
      array.push(this.createNoteGroup(), { emitEvent: false });
    } else {
      for (const entry of list) {
        array.push(this.createNoteGroup(entry), { emitEvent: false });
      }
    }
    this.ensureValueChangesSubscription();
  }

  addNote(): void {
    this.vcardArray.push(this.createNoteGroup());
    this.emitChanged();
  }

  removeNote(index: number): void {
    this.vcardArray.removeAt(index);
    if (this.vcardArray.length === 0) {
      this.vcardArray.push(this.createNoteGroup(), { emitEvent: false });
    }
    this.emitChanged();
  }

  hasDates(index: number): boolean {
    const row = this.vcardArray.at(index) as UntypedFormGroup;
    return !!(row.get("validFrom")?.value || row.get("validTo")?.value);
  }

  clearPeriod(index: number): void {
    const row = this.vcardArray.at(index) as UntypedFormGroup;
    row.patchValue({ validFrom: null, validTo: null });
    this.emitChanged();
  }

  getErrorKey(index: number): EmployeeVcardValidationError | null {
    return getVcardRowErrorKey(
      this.vcardArray.value as EmployeeVcardRowValue[],
      index,
    );
  }

  isValid(): boolean {
    return isEmployeeVcardRowsValid(
      this.vcardArray.value as EmployeeVcardRowValue[],
    );
  }

  getPayload(): EmployeeVcard[] {
    return employeeVcardRowsToPayload(
      this.vcardArray.value as EmployeeVcardRowValue[],
    );
  }

  private get vcardArray(): UntypedFormArray {
    const root =
      this.parentForm ??
      (this.controlContainer?.control as UntypedFormGroup | undefined);
    if (!root) {
      throw new Error(
        "app-employee-vcard requires [parentForm] inside a reactive form",
      );
    }
    const array = root.get("vcard");
    if (!(array instanceof FormArray)) {
      throw new Error('Parent form must define a "vcard" FormArray');
    }
    return array;
  }

  private createNoteGroup(entry?: EmployeeVcard): UntypedFormGroup {
    return new UntypedFormGroup({
      note: new UntypedFormControl(
        typeof entry?.note === "string" ? entry.note : "",
      ),
      validFrom: new UntypedFormControl(
        parseVcardDate(entry?.validFrom ?? null),
      ),
      validTo: new UntypedFormControl(parseVcardDate(entry?.validTo ?? null)),
    });
  }

  private ensureValueChangesSubscription(): void {
    if (this.valueChangesSubscribed) {
      return;
    }
    this.valueChangesSubscribed = true;
    this.vcardArray.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitChanged());
  }

  private emitChanged(): void {
    this.changed.emit();
  }
}

export function employeeVcardRowsToPayload(
  rows: EmployeeVcardRowValue[] | null | undefined,
): EmployeeVcard[] {
  return (rows ?? [])
    .map((row) => ({
      note: row.note?.trim() || null,
      validFrom: row.validFrom
        ? new Date(row.validFrom as Date).toISOString()
        : null,
      validTo: row.validTo ? new Date(row.validTo as Date).toISOString() : null,
    }))
    .filter((row) => row.note || row.validFrom || row.validTo);
}

export function isEmployeeVcardRowsValid(
  rows: EmployeeVcardRowValue[] | null | undefined,
): boolean {
  const list = rows ?? [];
  return list.every((_, index) => getVcardRowErrorKey(list, index) === null);
}

export function getVcardRowErrorKey(
  rows: EmployeeVcardRowValue[],
  index: number,
): EmployeeVcardValidationError | null {
  if (isDateRangeInvalid(rows[index])) {
    return "dateRangeInvalid";
  }
  const interval = getEntryInterval(rows, index);
  if (!interval) {
    return null;
  }
  if (interval === "indefinite" && countIndefinite(rows) > 1) {
    return "multipleIndefinite";
  }
  for (let j = 0; j < rows.length; j++) {
    if (j === index) {
      continue;
    }
    const other = getEntryInterval(rows, j);
    if (other && intervalsOverlap(interval, other)) {
      return "overlap";
    }
  }
  return null;
}

function isDateRangeInvalid(row: EmployeeVcardRowValue | undefined): boolean {
  const from = parseVcardDate(row?.validFrom ?? null);
  const to = parseVcardDate(row?.validTo ?? null);
  if (!from || !to) {
    return false;
  }
  return from.getTime() > to.getTime();
}

function getEntryInterval(
  rows: EmployeeVcardRowValue[],
  index: number,
): NoteInterval | null {
  const row = rows[index];
  if (!row) {
    return null;
  }
  const note = (row.note ?? "").trim();
  const from = parseVcardDate(row.validFrom ?? null);
  const to = parseVcardDate(row.validTo ?? null);
  if (!note && !from && !to) {
    return null;
  }
  if (!from && !to) {
    return "indefinite";
  }
  return {
    start: from ? toDayStart(from) : Number.NEGATIVE_INFINITY,
    end: to ? toDayEnd(to) : Number.POSITIVE_INFINITY,
  };
}

function countIndefinite(rows: EmployeeVcardRowValue[]): number {
  let count = 0;
  for (let i = 0; i < rows.length; i++) {
    if (getEntryInterval(rows, i) === "indefinite") {
      count++;
    }
  }
  return count;
}

function intervalsOverlap(a: NoteInterval, b: NoteInterval): boolean {
  if (a === "indefinite" || b === "indefinite") {
    return false;
  }
  return a.start <= b.end && b.start <= a.end;
}

function toDayStart(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function toDayEnd(date: Date): number {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function parseVcardDate(value: Date | string | null | undefined): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
